import {h, Fragment} from "preact"
import ProductCard from "./ProductCard";
import { useAppStateSelector, useLoadMore } from "@nosto/preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { isInfiniteScroll, batchSize } from "../config";

const isDesktop = () => typeof window !== 'undefined' && window.innerWidth >= 1024;

// Custom load-more button so we can control the classes/markup.
// useLoadMore(pageSize) returns { loadMore, href }; clicking appends the next
// batch (the library keeps the existing hits and fetches only the delta).
function LoadMoreButton({ pageSize }) {
  const { loadMore, href } = useLoadMore(pageSize);
  // No href => no more pages => hide the button.
  if (!href) return null;
  return (
    <div class="pgp-load-more-wrap">
      <a
        class="pgp-load-more button button--secondary ns-load-more-button"
        onClick={loadMore}
        href={href}
      >
        Load more
      </a>
    </div>
  );
}

export default function Products() {
  const { products, loading } = useAppStateSelector((state) => ({
    products: state.response.products,
    loading: state.loading,
  }));

  const [banners, setBanners] = useState([]);
  const [isNewLayout, setIsNewLayout] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.isNewLayout) {
      setIsNewLayout(true);
    }

    if (window.marketingBanners?.length) {
      const sorted = [...window.marketingBanners].sort((a, b) => {
        const aPos =
          a.collection_entry?.is_landscape
            ? Number(a.collection_entry?.position_banner_landscape || 0)
            : Number(a.collection_entry?.position_banner_normal || 0);
        const bPos =
          b.collection_entry?.is_landscape
            ? Number(b.collection_entry?.position_banner_landscape || 0)
            : Number(b.collection_entry?.position_banner_normal || 0);
        return aPos - bPos;
      });
      setBanners(sorted);
    }
  }, []);

  useEffect(() => {
    if (!isNewLayout || !gridRef.current) return;

    const grid = gridRef.current;
    let rafId;
    let ro;
    let mo;
    let attempts = 0;
    const MAX_ATTEMPTS = 120;

    const findSampleImage = () =>
      grid.querySelector('.grid__item:not(.marketing-banner-item) img');

    const applyHeight = () => {
      const sample = findSampleImage();
      if (!sample) return false;

      const h = sample.getBoundingClientRect().height;
      if (h > 0) {
        grid.style.setProperty('--product-card-height', `${h}px`);
        return true;
      }
      return false;
    };

    const pollUntilReady = () => {
      if (applyHeight()) return;

      attempts++;
      if (attempts < MAX_ATTEMPTS) {
        rafId = requestAnimationFrame(pollUntilReady);
      }
    };

    pollUntilReady();

    const setupResizeObserver = () => {
      const sample = findSampleImage();
      if (sample && !ro) {
        ro = new ResizeObserver(() => applyHeight());
        ro.observe(sample);
      }
    };

    mo = new MutationObserver(() => {
      if (!ro) setupResizeObserver();
      applyHeight();
    });
    mo.observe(grid, { childList: true, subtree: true });

    const handleGridChange = () => {
      attempts = 0;
      pollUntilReady();
    };
    window.addEventListener('gridColumnsChange', handleGridChange);

    const handleResize = () => applyHeight();
    window.addEventListener('resize', handleResize);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
      window.removeEventListener('gridColumnsChange', handleGridChange);
      window.removeEventListener('resize', handleResize);
    };
  }, [isNewLayout, products?.hits?.length, banners]);

  const renderBannerHTML = (banner) => {
    const {
      image,
      is_landscape,
      text_position,
      banner_heading,
      banner_heading_color,
      banner_heading_size,
      banner_heading_size_desktop,
      banner_description,
      banner_description_color,
      banner_description_size,
      banner_description_size_desktop,
      banner_cta_text,
      banner_cta_text_color,
      banner_cta_url,
    } = banner.collection_entry;

    const styleVars = [
      banner_heading_color &&
        `--banner-heading-color:${banner_heading_color}`,
      banner_heading_size &&
        `--banner-heading-size:${banner_heading_size}px`,
      banner_heading_size_desktop &&
        `--banner-heading-size-desktop:${banner_heading_size_desktop}px`,
      banner_description_color &&
        `--banner-description-color:${banner_description_color}`,
      banner_description_size &&
        `--banner-description-size:${banner_description_size}px`,
      banner_description_size_desktop &&
        `--banner-description-size-desktop:${banner_description_size_desktop}px`,
      banner_cta_text_color &&
        `--banner-cta-text-color:${banner_cta_text_color}`,
    ]
      .filter(Boolean)
      .join(";");

    let bannerContent = "";

    if (isNewLayout) {
      bannerContent = `
        ${
          banner_description
            ? `<p class="banner-description">${banner_description}</p>`
            : ""
        }
        ${
          banner_heading
            ? `<h2 class="banner-heading">${banner_heading}</h2>`
            : ""
        }
        ${
          banner_cta_text
            ? `<a href="${banner_cta_url}" class="banner-button" data-category="pgp-banner" data-action="promo_bar_click" data-label="${banner_heading || ""}">${banner_cta_text}</a>`
            : ""
        }
      `;
    }

    if (is_landscape) {
      return `
        <div class="marketing-banner landscape" style="${styleVars}">
          <div class="banner-image">
            <img src="${image}" alt="${banner_heading || ''}" />
          </div>
          <div class="banner-content">
            ${
              isNewLayout
                ? bannerContent
                : `
                <div class="banner-heading">
                  ${banner_heading ? `<h2 class="banner-heading">${banner_heading}</h2>` : ""}
                </div>
                <div class="banner-landscape-info">
                  ${banner_description ? `<p class="banner-description">${banner_description}</p>` : ""}
                  ${
                    banner_cta_text
                      ? `<a href="${banner_cta_url}" class="banner-button" data-category="pgp-banner" data-action="promo_bar_click" data-label="${banner_heading || ''}">${banner_cta_text}</a>`
                      : ""
                  }
                </div>
                `
              }
          </div>
        </div>
      `;
    } else {
      return `
        <div class="marketing-banner normal text-${text_position?.toLowerCase() || "center"}" style="${styleVars}">
          <div class="banner-image">
            <img src="${image}" alt="${banner_heading || ''}" />
          </div>
          <div class="banner-content">
            ${
              isNewLayout
                ? bannerContent
                : `
                <div class="banner-overlay">
                  <div class="banner-heading">
                    ${banner_heading ? `<h2 class="banner-heading">${banner_heading}</h2>` : ""}
                  </div>
                  ${
                    banner_cta_text
                      ? `<a href="${banner_cta_url}" class="banner-button" data-category="pgp-banner" data-action="promo_bar_click" data-label="${banner_heading || ''}"}>${banner_cta_text}</a>`
                      : ""
                  }
                </div>
                `
              }
          </div>
        </div>
      `;
    }
  };

  const gridClass = isNewLayout
    ? "grid product-grid search-product-grid new-layout grid--2-col-tablet-down"
    : "grid product-grid search-product-grid grid--2-col-tablet-down grid--3-col-desktop";

  // The grid markup, shared by both branches.
  const grid = (
    <div
      ref={gridRef}
      class={gridClass}
      style={loading ? "opacity: 0.3;" : ""}
    >
      {products.hits.map((hit, index) => {
          const bannerToInsert = banners.find((b) => {
            const entry = b.collection_entry;
            const bannerPosition = entry.is_landscape
              ? Number(entry.position_banner_landscape)
              : Number(entry.position_banner_normal);

            return bannerPosition === index;
          });

          // Stable key on the OUTER element so Preact keeps existing cards mounted
          // when the next batch is appended (prevents the whole grid from being
          // torn down and re-fetched, which caused the scroll-to-bottom jump).
          const itemKey = hit.productId || hit.url || index;

          return (
            <Fragment key={itemKey}>
              {bannerToInsert && (
                <div
                  class={`grid__item marketing-banner-item ${
                    bannerToInsert.collection_entry.is_landscape
                      ? "landscape"
                      : `normal text-${bannerToInsert.collection_entry.text_position?.toLowerCase() || "center"}`
                  }`}
                  dangerouslySetInnerHTML={{
                    __html: renderBannerHTML(bannerToInsert),
                  }}
                />
              )}
              <ProductCard
                className="grid__item"
                product={hit}
              />
            </Fragment>
          );
        })}
    </div>
  );

  // Infinite mode = button-only load more (no auto-scroll). The grid renders as
  // usual and a Load more button below it appends the next batch on click via
  // useLoadMore. Keeps existing hits mounted (stable Fragment keys above).
  if (isInfiniteScroll) {
    // Button-only "load more": no IntersectionObserver, so nothing loads until
    // the user clicks. The button calls useLoadMore itself and appends the batch.
    return (
      <>
        {grid}
        <LoadMoreButton pageSize={batchSize} />
      </>
    );
  }

  return grid;
}
